'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import { FC, useRef } from 'react';
import Card from '@/components/service-pages/TiltCardsGrid/Card';
import { TiltCardsGridDto } from '@/components/service-pages/TiltCardsGrid/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
const CARD_ICON_NAMES = ['green', 'silver', 'sexy', 'black', 'nice', 'fancy'] as const;
interface Props {
  data: TiltCardsGridDto;
  isIframe?: boolean;
}
const TiltCardsGrid: FC<Props> = t0 => {
  const $ = _c(18);
  const {
    data,
    isIframe
  } = t0;
  const sectionRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cardsRef = useRef(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const cardWrappersRef = useRef(t2);
  let t3;
  if ($[2] !== isIframe) {
    t3 = () => {
      if (isIframe) {
        return;
      }
      const titleLines = sectionRef.current?.querySelectorAll(`.${css.title} p`);
      const subtitle = sectionRef.current?.querySelector(`.${css.subtitle}`);
      if (titleLines?.length) {
        gsap.set(titleLines, {
          opacity: 0,
          y: 40,
          filter: "blur(10px)"
        });
      }
      if (subtitle) {
        gsap.set(subtitle, {
          opacity: 0,
          y: 20
        });
      }
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: titleLines,
          start: "top 80%",
          toggleActions: "play none none reverse"
        }
      });
      if (titleLines?.length) {
        tl.to(titleLines, {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 1.1,
          ease: "power3.out",
          stagger: 0.15
        });
      }
      if (subtitle) {
        tl.to(subtitle, {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out"
        }, "-=0.8");
      }
      const mm = gsap.matchMedia();
      mm.add({
        isMobile: "(max-width: 991px)",
        isDesktop: "(min-width: 992px)"
      }, context => {
        const {
          isMobile
        } = context.conditions as {
          isMobile: boolean;
        };
        if (!cardWrappersRef.current.length || !cardsRef.current.length) {
          return;
        }
        gsap.from(cardWrappersRef.current, {
          y: 80,
          opacity: 0,
          stagger: 0.12,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 70%"
          }
        });
        if (!isMobile) {
          cardsRef.current.forEach(card => {
            if (!card) {
              return;
            }
            context.add(() => {
              const move = e => {
                const r = card.getBoundingClientRect();
                const px = (e.clientX - r.left) / r.width - 0.5;
                const py = (e.clientY - r.top) / r.height - 0.5;
                gsap.to(card, {
                  rotateY: px * 30,
                  rotateX: -py * 30,
                  duration: 0.5,
                  ease: "power3.out",
                  overwrite: "auto"
                });
              };
              const leave = () => {
                gsap.to(card, {
                  rotateX: 0,
                  rotateY: 0,
                  duration: 0.5,
                  ease: "power3.out",
                  overwrite: "auto"
                });
              };
              card.addEventListener("mousemove", move, {
                passive: true
              });
              card.addEventListener("mouseleave", leave);
              return () => {
                card.removeEventListener("mousemove", move);
                card.removeEventListener("mouseleave", leave);
              };
            });
          });
        }
      });
      return () => mm.revert();
    };
    $[2] = isIframe;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== isIframe) {
    t4 = {
      scope: sectionRef,
      dependencies: [isIframe]
    };
    $[4] = isIframe;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useGSAP(t3, t4);
  let t5;
  if ($[6] !== data.title) {
    t5 = <Typography uppercase={true} variant="h2" tag="h2" className={css.title}><StrapiRichContent content={data.title} /></Typography>;
    $[6] = data.title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== data.subtitle) {
    t6 = <Typography variant="body-m" tag="p" className={css.subtitle}>{data.subtitle}</Typography>;
    $[8] = data.subtitle;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== data.cards) {
    t7 = data.cards.map((card_0, index) => <div key={index} ref={el => {
      if (el) {
        cardWrappersRef.current[index] = el;
      }
    }} className={css.cardTiltHost}><Card ref={el_0 => {
        if (el_0) {
          cardsRef.current[index] = el_0;
        }
      }} title={card_0.title} text={card_0.text} iconName={CARD_ICON_NAMES[index]} /></div>);
    $[10] = data.cards;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = <div className={css.grid}>{t7}</div>;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t8) {
    t9 = <section ref={sectionRef} className={css.section}><div className={css.container}>{t5}{t6}{t8}</div></section>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};
export default TiltCardsGrid;